// Header toolbar in the middle of the layout: Pause, Eraser, Clear,
// plus the live HUD (tick / backend / material count).

import { useAuth } from "../auth/AuthProvider";
import type { SandboxLike } from "../sim/types";

export interface ToolbarProps {
  paused: boolean;
  onTogglePaused: () => void;
  eraser: boolean;
  onToggleEraser: () => void;
  onClear: () => void;
  onStep: () => void;
  onCycleBackground: () => void;
  backgroundLabel: string;
  sandbox: SandboxLike | null;
  tick: number;
  backend: string;
  materialCount: number;
  /** Open the signup/signin modal in the given mode. The toolbar
   * doesn't manage modal state itself — it just signals up. */
  onOpenAuth: (mode: "signin" | "signup") => void;
  /** Mobile drawer state + toggles. Desktop hides these buttons via
   * CSS — the sidebars are always docked there. */
  leftNavOpen: boolean;
  rightNavOpen: boolean;
  onToggleLeftNav: () => void;
  onToggleRightNav: () => void;
}

export function Toolbar(props: ToolbarProps) {
  const disabled = !props.sandbox;
  const { user, signout } = useAuth();
  return (
    <header className="toolbar">
      <button
        type="button"
        className={"nav-toggle nav-toggle-left" + (props.leftNavOpen ? " active" : "")}
        onClick={props.onToggleLeftNav}
        aria-label={props.leftNavOpen ? "Close materials panel" : "Open materials panel"}
        aria-expanded={props.leftNavOpen}
        title="Materials panel"
      >
        <span aria-hidden="true">☰</span>
      </button>
      <div className="toolbar-group">
        <button
          type="button"
          className={"toolbar-button" + (props.paused ? " active" : "")}
          onClick={props.onTogglePaused}
          disabled={disabled}
          title={props.paused ? "Resume the simulation (space bar)" : "Pause the simulation (space bar)"}
          aria-label={props.paused ? "Resume" : "Pause"}
        >
          <span className="toolbar-icon" aria-hidden="true">
            {props.paused ? "▶" : "❚❚"}
          </span>
          <span className="toolbar-label">{props.paused ? "Play" : "Pause"}</span>
        </button>
        <button
          type="button"
          className="toolbar-button"
          onClick={props.onStep}
          disabled={disabled || !props.paused}
          title="Advance one tick while paused"
          aria-label="Step"
        >
          <span className="toolbar-icon" aria-hidden="true">⏭</span>
          <span className="toolbar-label">Step</span>
        </button>
        <button
          type="button"
          className={"toolbar-button" + (props.eraser ? " active" : "")}
          onClick={props.onToggleEraser}
          disabled={disabled}
          title="Toggle eraser (paints empty)"
          aria-label="Eraser"
        >
          <span className="toolbar-icon" aria-hidden="true">
            {/* Tilted pink eraser. The ⌫ keyboard glyph reads as
             * "backspace" rather than "eraser tool", which the
             * user flagged. This SVG matches the classic
             * Powder-Toy / Sandspiel eraser shape. */}
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <g transform="rotate(-30 8 8)">
                <rect
                  x="2"
                  y="6"
                  width="12"
                  height="5"
                  rx="1"
                  fill="#ff8aa6"
                  stroke="#d24f6e"
                  strokeWidth="1"
                />
                <rect
                  x="2"
                  y="6"
                  width="4"
                  height="5"
                  fill="#d24f6e"
                />
              </g>
            </svg>
          </span>
          <span className="toolbar-label">Eraser</span>
        </button>
        <button
          type="button"
          className="toolbar-button danger"
          onClick={props.onClear}
          disabled={disabled}
          title="Clear the entire grid"
          aria-label="Clear"
        >
          <span className="toolbar-icon" aria-hidden="true">🗑</span>
          <span className="toolbar-label">Clear</span>
        </button>
        <button
          type="button"
          className="toolbar-button"
          onClick={props.onCycleBackground}
          title={`Cycle the canvas background (current: ${props.backgroundLabel})`}
          aria-label={`Background: ${props.backgroundLabel}`}
        >
          <span className="toolbar-icon" aria-hidden="true">◐</span>
          <span className="toolbar-label">BG: {props.backgroundLabel}</span>
        </button>
        {user ? (
          <button
            type="button"
            className="toolbar-button toolbar-button-text"
            onClick={signout}
            title={`Signed in as ${user.email}. Tap to sign out.`}
            aria-label="Sign out"
          >
            {/* Always-text auth button: no `.toolbar-icon` /
             * `.toolbar-label` split, so it stays as text on
             * mobile too instead of collapsing to an avatar
             * glyph. Two label spans let CSS swap the desktop
             * form ("user · Sign out") for a tighter mobile
             * form ("Sign out") without rerunning the React
             * tree on viewport changes. */}
            <span className="toolbar-text-desktop">
              {user.email.split("@")[0]} · Sign out
            </span>
            <span className="toolbar-text-mobile">Sign out</span>
          </button>
        ) : (
          <button
            type="button"
            className="toolbar-button toolbar-button-text"
            onClick={() => props.onOpenAuth("signin")}
            title="Sign in to save experiments and formulas (optional)"
            aria-label="Sign in / Sign up"
          >
            <span className="toolbar-text-desktop">Sign in / Sign up</span>
            <span className="toolbar-text-mobile">Signin/Signup</span>
          </button>
        )}
      </div>

      <div className="toolbar-hud">
        <span>tick {props.tick.toLocaleString()}</span>
        <span>{props.backend}</span>
        <span>{props.materialCount} materials</span>
      </div>
      <button
        type="button"
        className={"nav-toggle nav-toggle-right" + (props.rightNavOpen ? " active" : "")}
        onClick={props.onToggleRightNav}
        aria-label={
          props.rightNavOpen
            ? "Close formulas and experiments panel"
            : "Open formulas and experiments panel"
        }
        aria-expanded={props.rightNavOpen}
        title="Formulas + experiments panel"
      >
        <span aria-hidden="true">☰</span>
      </button>
    </header>
  );
}
